export default {
  partCards: {
    partHeatNote:
      'Heat is applied to the part itself, excluding extra material added by wrapping.',
    missingContact: 'Contact not made',
    cadVolume: 'CAD volume',
    meshVolume: 'Meshed volume',
    centre: 'CAD centre',
    unmeasured: 'Not measured',
    perfect: 'Perfect contact',
    gap: 'Maximum CAD gap',
    perfectBanner:
      'Part-to-part contacts transfer heat perfectly; contact resistance is not modelled.',
    volumeWarning:
      'The meshed volume differs from the CAD volume by more than 10%. Check this W/m³ source; use total watts to specify the part’s power.',
  },
  meshSweeps: {
    title: 'Mesh Sweeps',
    gciLabel: 'Grid Convergence Index',
    comingSoon: 'Coming soon',
    gciDescription:
      'Sweeps element size at the current Max Size, scaled by {{ratio}}× over {{levels}} grids to estimate discretization error.',
    advanced: 'Advanced',
    refinementRatio: 'Refinement ratio',
    levels: 'Levels',
  },
  fea: {
    material: 'Material',
    defaultMaterial: 'Default material',
    defaultMaterialHint:
      'Submitted as the material for every body without a per-body override. Assembly assignments below replace it for their volume.',
    modes: 'Modes',
    numberOfModes: 'Number of modes',
    numberOfModesHint: 'Positive integer from 1 to 50.',
    bucklingHint:
      'Factors multiply this load pattern. Linear buckling is an optimistic upper bound, not a collapse-load prediction.',
    condition: 'Condition',
    density: 'Density',
    elasticModulus: 'Elastic Modulus',
    poissonsRatio: "Poisson's Ratio",
    yieldStrength: 'Yield Strength',
    tensileStrength: 'Tensile Strength',
    sweepAxes: 'DOE sweep axes',
    sweepAxesHint:
      'Enter several values on a row to vary that input across runs. The load and contact numbers above stay as the baseline; only rows you expand here change per design point.',
    seedSweeping:
      'This component is sweeping — edit the list under DOE sweep axes.',
    // Localized material-picker labels. Keys mirror `STRUCTURAL_MATERIALS` in
    // `src/constants/structuralMaterials.gen.ts`; that file's LABELS map stays
    // the English canonical (kept in the picker's tooltip so the alloy name
    // stays discoverable in any locale). Non-EN locales translate the noun
    // and keep the alloy code — polymers / proprietary names (PEEK, PTFE,
    // Inconel 718, PC/ABS) pass through unchanged because their code IS the
    // technical name.
    materials: {
      steel_304: 'Steel 304',
      carbon_steel_1045: 'Carbon Steel 1045',
      steel_structural: 'Structural Steel',
      aluminum_6061: 'Aluminum 6061',
      aluminum_7075: 'Aluminum 7075',
      titanium_ti6al4v: 'Titanium Ti-6Al-4V',
      copper_c110: 'Copper C110',
      brass_c360: 'Brass C360',
      inconel_718: 'Inconel 718',
      pcabs: 'PC/ABS',
      polycarbonate: 'Polycarbonate',
      abs: 'ABS',
      nylon_66: 'Nylon 66',
      peek: 'PEEK',
      ptfe: 'PTFE',
      hdpe: 'HDPE',
      // CHT-only thermal-library materials (not in the structural picker,
      // but they share the same picker pattern).
      aluminum_nitride: 'Aluminum Nitride',
      diamond_cvd: 'Diamond (CVD)',
      still_air: 'Still air',
      silicon: 'Silicon',
      silicon_dioxide: 'Silicon dioxide',
      alumina: 'Alumina',
      silicone: 'Silicone',
      acrylic: 'Acrylic',
      aluminum_5052: 'Aluminum 5052',
      tungsten: 'Tungsten',
    },
    // Expanded / plain-language names for abbreviated materials — surfaced as
    // a muted suffix on the picker row so a user unfamiliar with the polymer
    // acronym or proprietary trade name can still recognize what they're
    // picking. Structural materials (peek/ptfe/hdpe/abs/pcabs/inconel_718)
    // now source this from `StructuralProperties.description` in
    // structuralMaterials.gen.ts instead — see material-labels.ts. This map
    // only still carries the THERMAL_MATERIAL_LIBRARY entries, which are not
    // in the structural library and so were out of scope for that migration.
    materialsFull: {
      aluminum_nitride: 'Thermal-management ceramic',
      diamond_cvd: 'Synthetic diamond heat spreader',
      // silicon / silicon_dioxide / silicone / aluminum_5052 / tungsten:
      // omit — display names are already plain English.
      alumina: 'Aluminum oxide ceramic',
      acrylic: 'Polymethyl methacrylate (PMMA)',
    },
  },
  feaTemplate: {
    structural: 'Structural',
    modal: 'Modal',
    buckling: 'Buckling',
    thermal: 'Thermal',
    thermalRegime: 'Thermal solve type',
    thermalSteady: 'Steady',
    thermalTransient: 'Transient',
  },
  thermal: {
    defaultMaterial: 'Solid material',
    materialsByVolume: 'Material per volume',
    defaultMaterialHint:
      'Default solid material for every volume. Per-volume pickers below override it when the mesh exposes Volume_* bodies.',
    defaultConvectionH: 'Default film coefficient',
    defaultConvectionHHint:
      'Ambient convection h used when a face is set to Convection. Not a solid material property — natural air ~5–25, forced air ~50–250, water ~1000+ W/(m²·K).',
    syncConvectionH: 'Apply h to {{count}} convection faces',
    syncConvectionHEmpty: 'No convection faces to update yet.',
    syncConvectionHTooltip:
      'Set film coefficient on every convection face to {{h}} W/(m²·K).',
    initialTemperature: 'Initial temperature',
    initialTemperatureHint:
      'Uniform temperature of the solid at the start of the run, applied to every node of every solid volume (Nall). Not a face boundary condition — face loads and convection sit on the BC rows.',
    initialTemperatureVolumes:
      'Uniform on every node of {{volumes}}. Not a face boundary condition — face loads and convection sit on the BC rows above.',
    initialTemperatureAllVolumes:
      'Uniform on every node of every solid volume (Nall). Not a face boundary condition — face loads and convection sit on the BC rows above.',
    solverTitle: 'Steady heat transfer',
    solverBody:
      'The solver iterates until the temperature field settles. Overview grades converged from the residual check and heat balance from integrated boundary fluxes — there are no editable residual targets in Draft.',
    transientSolverTitle: 'Transient heat transfer',
    transientSolverBody:
      'The solver advances physical time. Overview grades converged from the period gate, energy balance (J), and the maximum-principle temperature bounds check — there are no editable residual targets in Draft.',
  },
  feaSolveLoop: {
    helpLabel: 'How step, increment, and iteration nest',
    step: 'Step',
    increment: 'Increment',
    iteration: 'Iteration',
    // Counted SVG labels (ENG-3002): results use "ran", draft uses "requested".
    stepRan: 'Step · {{count}} ran',
    stepRequested: 'Step · {{count}} requested',
    incrementRan: 'Increment · {{count}} ran',
    incrementRequested: 'Increment · {{count}} requested',
    iterationRan: 'Iteration · max {{count}}',
    iterationRequested: 'Iteration · max {{count}} requested',
    stepHelp: 'One analysis procedure covering the full time period.',
    incrementHelp:
      'One advance of physical time inside the step (the Δt fields below).',
    iterationHelp:
      'Newton equilibrium tries inside an increment until residuals settle.',
  },
  transientTime: {
    title: 'Time schedule',
    hint: 'How long the heat-transfer step runs and how the solver advances through it. Blank optional fields use automatic defaults.',
    period: 'Cycle period (s)',
    periodHint:
      'Total physical time for this heat-transfer step — the full cycle the solver must reach before the run is complete.',
    initialIncrement: 'First increment (s)',
    initialIncrementHint:
      'Size of the first time step. Leave blank for one-hundredth of the cycle period. A larger first step helps when a sudden boundary change would otherwise undershoot on a fine mesh.',
    minIncrement: 'Min increment (s)',
    minIncrementHint:
      'Smallest time step the solver may use when cutting back. Leave blank for the automatic floor.',
    maxIncrement: 'Max increment (s)',
    maxIncrementHint:
      'Largest time step allowed as the solver grows increments. Leave blank for one-tenth of the cycle period.',
    outputInterval: 'Output interval (s)',
    outputIntervalHint:
      'How often temperature frames are written for the results timeline. Leave blank to match the first increment (about 101 frames; never denser than about 200).',
    maxTemperatureChange: 'Max ΔT per step (K)',
    maxTemperatureChangeHint:
      'Largest temperature change allowed in one increment. The solver cuts the step if any node would move more than this. Leave blank to let the increment bounds alone govern.',
    fixedIncrements: 'Fixed increments',
    fixedIncrementsHint:
      'Use a fixed time-step size (no automatic cutback). Off by default so the solver can shrink steps when the residual stalls.',
    maxIncrements: 'Max increments',
    maxIncrementsHint:
      'Ceiling on the number of time steps in this heat-transfer step. Default 1000.',
    autoPlaceholder: 'Auto',
  },
  feaAdvancedSummary:
    'Saved structural solver inputs. Edit materials, boundary conditions, contacts, and preloads in Physics & boundary conditions.',
  forceVector: {
    cannotBeZero: 'Force vector cannot be zero',
  },
  direction: {
    label: 'Direction',
    auto: 'Auto — flow axis ({{axis}})',
    studyDefault: 'Study default',
    custom: 'Custom (x, y, z)',
    cannotBeZero: 'Direction vector cannot be zero',
    normalizedNote: 'Normalized to a unit vector at run time.',
    tooltip:
      'Inlet flow direction. Applies to every run in the study — a velocity sweep varies speed only, direction stays fixed across runs. Options: Auto follows the detected flow axis; ±X/±Y/±Z snap to an axis; Custom lets you type an (x, y, z) vector (normalized at run time).',
  },
  rasWarnings: {
    wallResolvingLead: 'k-ω SST is wall-resolving',
    wallResolvingBodyPre:
      'selected mesh has no boundary layer. For accurate results, regenerate the mesh in MeshGen with a boundary layer targeting',
    wallResolvingBodyPost:
      'Solver will still run, but near-wall physics will be wrong.',
    wallFunctionsLead: '{{model}} uses wall functions',
    wallFunctionsBodyPre: 'ideal',
    wallFunctionsBodyPost:
      'Selected mesh has no boundary layer; results may be inaccurate. Consider regenerating with a wall-function-targeted BL.',
    lengthScaleLead: 'Turbulent length scale not set',
    lengthScaleBodyPre: 'backend falls back to',
    lengthScaleBodyPost:
      "which is too large for most CAD parts. Set to ~7–10% of the body's characteristic length (chord, diameter, span) for more accurate initial ω / ε.",
  },
  advanced: {
    title: 'Advanced',
    convergenceCriteria: 'Convergence Criteria',
    solverSettings: 'Solver Settings',
    forceCoeffsTitlePrefix: 'Force Coefficients',
    refArea: 'Ref Area (m²)',
    refChord: 'Ref Chord (m)',
    egValue: 'e.g. {{value}}',
    forceCoeffsHintPrefix: 'Leave blank to skip',
    forceCoeffsHintSuffix: 'omitted from results',
    refAreaInfo:
      'Projected frontal area used to normalize aerodynamic forces into C_D / C_L. For airfoils this is chord × span; for a bluff body it is the area facing the flow. Units: m².',
    refChordInfo:
      'Characteristic length used to normalize the pitching moment into C_M — usually the airfoil chord. Units: m.',
    presetsLabel: 'Presets',
    presetFluent: 'Match Fluent / STAR-CCM+',
    presetLowRe: 'Low-Re / viscous sublayer',
  },
  // OpenFOAM SIMPLE-algorithm override labels + tooltips. Consumed by
  // `AdvancedSolverSettings` (draft edit grid), `SolverConfigSummary`
  // (details-panel key/value rows) and `DesignPointsTable` (header
  // humanizer). Copy per panel.md: natural-language description of what
  // the knob does + when to tune it. OpenFOAM API tokens (dictionary
  // keys, entry paths) stripped — engineering terms of art that
  // simulation engineers know (SIMPLE, laminar, RAS, snappyHexMesh,
  // hex/tet, upwind) stay.
  openFoamOverrides: {
    // Rendered on the DP methodology / report surface for `widget: 'checkbox'`
    // fields (`simple_consistent`, `write_compression`). The wire carries the
    // raw bool; the reader gets `On` / `Off` instead of `true` / `false`.
    booleans: {
      on: 'On',
      off: 'Off',
    },
    endTime: {
      label: 'Max Iterations',
      description:
        'Hard cap on solver iterations. The run stops at this cap or when the residual targets below are met, whichever comes first. Default: 1000 (laminar) / 2000 (RAS).',
    },
    maxRunTime: {
      label: 'Time Limit (h)',
      description:
        'The longest one run may take, in hours. When the time is up the solver stops cleanly and saves its progress, so you can continue the run later. Default: 4 h, at most 11 h.',
      outOfRange: 'Choose a time limit between 1 minute and 11 hours.',
    },
    residualControlP: {
      label: 'Residual p',
      description:
        'How tight the pressure residual has to get before the run is considered converged. Lower = tighter convergence, longer runtime. Default: 1e-4.',
    },
    residualControlU: {
      label: 'Residual U',
      description:
        'How tight the velocity residual has to get before the run converges. Velocity is harder to converge than pressure — keep above ~1e-4 unless you have a reason. Default: 5e-3.',
    },
    writeInterval: {
      label: 'Write Interval',
      description:
        'How often (in iterations) the solver writes a result snapshot to disk. Lower values give finer residual plots at the cost of disk usage. Default: 50.',
    },
    nNonOrthCorrectors: {
      label: 'Non-Orth Correctors',
      description:
        'Extra pressure-correction passes per iteration to compensate for skewed cells. Raise to 3–4 on highly skewed tet meshes; leave at 2 on hex-dominant meshes. Default: 2.',
    },
    relaxP: {
      label: 'Relax p',
      description:
        'Under-relaxation for pressure — how much of the newly computed pressure is blended in each iteration. Lower = more stable but slower to converge. Default: 0.3 (laminar) / 0.2 (RAS).',
    },
    relaxU: {
      label: 'Relax U',
      description:
        'Under-relaxation for velocity. Lower = more stable but slower to converge; 0.5 balances the two on most cases. Default: 0.5.',
    },
    relaxTurbulence: {
      label: 'Relax Turbulence',
      description:
        'Under-relaxation for the turbulence fields (k and its dissipation). Lower = more stable, slower to converge; drop to 0.3 if the turbulence values go unstable. RAS only — has no effect on laminar. Default: 0.5.',
    },
    gradScheme: {
      label: 'Grad Scheme',
      description:
        'How the solver approximates gradients across cells. "Unlimited" is fastest and accurate on near-orthogonal hex meshes; the cell-limited options damp gradients on skewed tet meshes to prevent oscillation. Default: unlimited.',
      options: {
        unlimited: 'Unlimited',
        cell_limited_half: 'Cell-limited (half)',
        cell_limited_full: 'Cell-limited (full)',
      },
    },
    divScheme: {
      label: 'Div Scheme',
      description:
        'How the solver discretizes momentum convection. "Linear upwind" is 2nd-order and stable — the default. "Upwind" is 1st-order for marginal meshes; "limited linear" is for shock or steep-gradient cases. Default: linear upwind.',
      options: {
        upwind: 'Upwind (1st order)',
        linear_upwind: 'Linear upwind (2nd order)',
        limited_linear: 'Limited linear',
        linear: 'Linear (central)',
      },
    },
    linearSolvers: {
      label: 'Linear Solvers',
      description:
        'Per-field linear-solver overrides. Advanced power-user setting; not editable in this panel today.',
    },
    divSchemeTurbulence: {
      label: 'Div Scheme (turbulence)',
      description:
        'Convection scheme for k / ω / ε. "Upwind" is 1st-order stable — the default. The 2nd-order options match Fluent / STAR-CCM+ defaults.',
    },
    pInterpolation: {
      label: 'p Interpolation',
      description:
        'Face interpolation for pressure. Leave blank for the block default (linear). "midPoint" / "cubic" emit an explicit `interpolate(p)` entry in fvSchemes.',
      options: {
        linear: 'Linear',
        midPoint: 'Midpoint',
        cubic: 'Cubic',
      },
    },
    nutWallFunction: {
      label: 'ν_t Wall Function',
      description:
        'Turbulent-viscosity wall function. Default handles the log-law region; Spalding blends for the buffer zone; LowRe leaves the viscous sublayer to the mesh.',
      options: {
        nutkWallFunction: 'nutkWallFunction (log-law)',
        nutUSpaldingWallFunction: 'nutUSpaldingWallFunction (blended)',
        nutLowReWallFunction: 'nutLowReWallFunction (viscous sublayer)',
      },
    },
    kWallFunction: {
      label: 'k Wall Function',
      description:
        'Turbulent kinetic energy wall function. Default is stable across log-law + buffer; LowRe for wall-resolving meshes.',
      options: {
        kqRWallFunction: 'kqRWallFunction (default)',
        kLowReWallFunction: 'kLowReWallFunction (viscous sublayer)',
        fixedValue: 'fixedValue (prescribed BC)',
      },
    },
    omegaWallFunction: {
      label: 'ω Wall Function',
      description:
        'Specific-dissipation wall function. Default is the all-y+ blended form; `fixedValue` is for prescribed BC comparisons.',
      options: {
        omegaWallFunction: 'omegaWallFunction (all-y+)',
        fixedValue: 'fixedValue (prescribed BC)',
      },
    },
    epsilonWallFunction: {
      label: 'ε Wall Function',
      description:
        'Dissipation-rate wall function. Used by the k-ε turbulence model; LowRe for viscous-sublayer meshes.',
      options: {
        epsilonWallFunction: 'epsilonWallFunction (log-law)',
        epsilonLowReWallFunction: 'epsilonLowReWallFunction (viscous sublayer)',
      },
    },
    residualControlTurbulence: {
      label: 'Residual (turbulence)',
      description:
        'How tight the turbulence residuals (k + ω or ε) have to get before the run is considered converged. Default: 1e-4.',
    },
    laplacianLimiter: {
      label: 'Laplacian Limiter',
      description:
        'Face-gradient limiter for laplacian + snGrad schemes. 1.0 = full explicit correction (matches Fluent / STAR-CCM+); default 0.33 damps for stability on skewed meshes.',
    },
    simpleConsistent: {
      label: 'SIMPLE consistent',
      description:
        'When on, uses the SIMPLE-consistent formulation (default). Turn off to match Fluent / STAR-CCM+ defaults. External flow only.',
    },
    prefCell: {
      label: 'pRef Cell',
      description:
        'Index of the cell pinning the pressure reference on closed domains. Leave at 0 unless the case notes a different cell.',
    },
    prefValue: {
      label: 'pRef Value',
      description:
        'Pressure value pinned at pRef Cell. Kinematic pressure (m²/s²) on incompressible; leave at 0 unless the case sets a gauge reference.',
    },
    writeFormat: {
      label: 'Write Format',
      description:
        'controlDict output format. Binary is ≈10× smaller time-step directories at multi-M cells.',
      options: {
        ascii: 'ASCII (default, human-readable)',
        binary: 'Binary (compact)',
      },
    },
    writePrecision: {
      label: 'Write Precision',
      description:
        'Decimal digits per number written to the case files. Raise to 15 for Cd/Cl parity comparisons against other solvers. Default: 6.',
    },
    writeCompression: {
      label: 'Write Compression',
      description:
        'zlib compression on the case output. Emits `on` / `off` in controlDict; useful for long RAS runs where disk is tight.',
    },
  },
  // Gradient-scheme + linear-solver override preview. Gated OFF by default
  // (VITE_SIMOPS_SOLVER_SCHEMES) — the backend hardcodes these today, so the
  // section is a non-submitting preview until the BE contract lands.
  schemesOverride: {
    title: 'Discretization & Linear Solver',
    previewBanner:
      'Preview — the backend does not accept these yet. Values are not saved or sent. Ready to enable once the solver-scheme contract ships.',
    gradientGroup: 'Gradient control',
    linearSolverGroup: 'Linear solver',
    gradLimited: {
      label: 'Limit gradients',
      description:
        'Damp cell-to-cell gradients instead of using an unlimited scheme. Trades a little accuracy for robustness on skewed cells — recommended on tet or poly meshes.',
    },
    gradLimiterCoeff: {
      label: 'Limiter coeff',
      description:
        'How aggressively to clamp gradients: 0 = no clamping, 1 = full clamping. Only used when "Limit gradients" is on.',
    },
    pSolver: {
      label: 'Pressure solver',
      placeholder: 'GAMG (default)',
      description:
        'Linear solver for the pressure equation. GAMG (geometric-algebraic multigrid) is the default and fastest for most cases; PCG and PBiCGStab are Krylov alternatives.',
    },
    pTolerance: { label: 'p tolerance' },
    pRelTol: { label: 'p relTol' },
    uTolerance: { label: 'U tolerance' },
    uRelTol: { label: 'U relTol' },
    errors: {
      limiterCoeffRange: 'Limiter coefficient must be between 0 and 1.',
      tolerancePositive: 'Tolerance must be greater than 0.',
      relTolRange: 'Relative tolerance must be between 0 and 1.',
    },
  },
  simTypeFromMesh: {
    fromMesh: 'set in MeshGen',
    internal: 'internal',
    external: 'external',
    tooltip:
      'The mesh you picked was built for this simulation type in MeshGen, so the study follows it automatically. To run a different type, create a new mesh in MeshGen.',
    mixed:
      "The picked meshes were built for different simulation types — pick the type to run, or drop the meshes that don't match.",
  },
  simType: {
    cfd: {
      title: 'Computational Fluid Dynamics.',
      summary:
        'Solves steady-state incompressible flow (velocity, pressure, turbulence) with k-ω SST.',
      mesh: 'Mesh: single-region fluid mesh (tet or hex). Boundary-layer inflation on walls — y⁺ ≲ 1 for resolved walls or y⁺ ≈ 30–300 with wall functions. Inlet / outlet / wall patches tagged in MeshGen.',
    },
    fea: {
      title: 'Finite Element Analysis.',
      summary:
        'Solid mechanics — stress, strain, and deformation under static loads.',
      mesh: 'Mesh: solid-only tet or hex with refined cells around contact / load-bearing surfaces and stress concentrators.',
    },
    cht: {
      title: 'Conjugate Heat Transfer.',
      summary:
        'Couples fluid flow with solid heat conduction — reports per-region temperatures and the peak hot-spot temperature.',
      mesh: 'Mesh: multi-region tet mesh with one fluid region and one or more solids. Each region is meshed and solved separately, with heat passing across the fluid-solid interface. Tagged faces drive inlet, outlet, fixed-temperature, heat-flux, or convection boundary conditions.',
    },
  },
  form: {
    inletSweep: 'Inlet sweep',
    outletSweep: 'Outlet sweep',
    velocity: 'Velocity',
    velocityNote:
      'Speed along the selected direction. Commas make a sweep — one run per value (1, 2, 3 → 3 runs), not a 3D vector.',
    pressure: 'Pressure',
    pressureNote: 'Gauge pressure — 0 = ambient reference (1 atm absolute)',
    sweepFieldInfoAria: 'How sweep fields generate design points',
    sweepFieldInfo:
      'How this field becomes design points:\n\n• Single value → 1 run at that value.\n• Comma-separated list → 1 run per value.\n• Multiple sweep fields → every combination runs (2 × 3 → 6 runs).\n\nSweepable in CFD today: inlet velocity, outlet pressure, and any two of density ρ / dynamic viscosity μ / kinematic viscosity ν (the third is derived, ν = μ/ρ).\n\nStudy defaults are the values in these fields. Every design point uses these values UNLESS it explicitly overrides them. So editing a field here changes the whole sweep\'s default — there is no separate "save as default" action.\n\nManual DPs are extra pinned points appended after the sweep. They do not multiply the cartesian (one manual DP = one extra run), and any field the manual DP leaves blank inherits the study default here.',
    flowType: 'Flow Type',
    // Turbulence-model dropdown labels. English keeps the pure technical
    // term (users search literature for these); non-EN locales prepend a
    // natural-language noun so a first-time user knows what the row is.
    flowTypeOptions: {
      laminar: 'Laminar',
      k_epsilon: 'k-ε (k-epsilon)',
      k_omega: 'k-ω (k-omega)',
      k_omega_sst: 'k-ω SST',
    },
    flowTypeShort: {
      laminar: 'Laminar',
      k_epsilon: 'k-ε',
      k_omega: 'k-ω',
      k_omega_sst: 'k-ω SST',
    },
    turbulence: 'Turbulence',
    intensity: 'Intensity',
    intensityTooltip:
      'Ratio of velocity fluctuation to mean flow (u′/U). Sets initial turbulent kinetic energy k. Typical: 0.01–0.05 external, 0.05–0.10 internal.',
    lengthScale: 'Length Scale (m)',
    lengthScaleTooltip:
      "Characteristic eddy size used to set initial ω and ε. Rule of thumb: ~7% of the geometry's characteristic length (chord, diameter). Defaults to 0.1 m.",
    flowTypeDefaults: '{{label}} defaults',
    clickToView: '· click to view',
    // Copy for the collapsed FlowTypeDefaultsCard under Flow Type. `notes` is
    // the always-visible one-line guidance per turbulence model; `labels` +
    // `values` drive the k/v table that appears when the card is expanded.
    // Numeric values (`1000`, `0.05`, `1e-4 / 5e-3`) stay in the constants
    // table (locale-invariant); only prose lives here.
    flowTypeDefaultsCard: {
      notes: {
        laminar:
          'No turbulence model. Suitable for Re ≲ 2,000 (internal) or low-Re external flows.',
        k_epsilon:
          'Standard k-ε. Robust in the freestream but inaccurate near walls without wall functions — use only if y+ is in the log-law band (30–300).',
        k_omega:
          'Wilcox k-ω. Better near-wall behaviour than k-ε but sensitive to freestream ω boundary values. Pick SST for most external aero cases.',
        k_omega_sst:
          'Menter k-ω SST. Blends k-ω in the boundary layer with k-ε in the freestream — the safe default for most external aero and internal-flow CFD. Resolves to wall (y+ ≲ 1) or uses wall functions (y+ ≳ 30).',
      },
      labels: {
        maxIterations: 'Max iterations',
        residualPU: 'Residual p / U',
        writeInterval: 'Write interval',
        nonOrthCorrectors: 'Non-orth correctors',
        relaxPU: 'Relax p / U',
        turbulenceIntensity: 'Turbulence intensity',
        lengthScale: 'Length scale',
      },
      values: {
        lengthScaleHint: 'user-set (≈ 7 % of body length)',
      },
    },
  },
  unsupported: {
    title: '{{template}} is not yet editable in the UI.',
    body: 'Studies with this template were created via the API or a previous version of the FE. View the saved configuration in the report; to edit, switch the study type via the toggle above.',
  },
  fluidProps: {
    fluid: 'Fluid',
    density: 'Density',
    dynamicViscosity: 'Dynamic Viscosity',
    kinematicViscosity: 'Kinematic Viscosity',
    derivedFrom: 'derived from {{a}} and {{b}}',
    pinDerivedTip:
      "{{field}} is derived. Click another field's link icon to derive that one instead.",
    pinUserDefinedTip:
      '{{field}} is user-defined. Click to make it the derived field.',
    // Fluid-preset picker labels. Keys mirror `FLUID_PRESET_LABELS` in
    // `src/data/fluid-presets.ts` — that map stays the English canonical
    // (surfaced as a muted inline suffix so the fluid noun is discoverable
    // in any locale). Reference conditions (20 °C) sit inside the label so
    // a future "Air (60 °C)" preset renders naturally.
    presets: {
      air: 'Air (20 °C)',
      water: 'Water (20 °C)',
      custom: 'Custom',
    },
  },
  sweepField: {
    // Accessible names for the two comboboxes inside a sweep field whose
    // only content is their own current value (ENG-1733). `unitAria` is the
    // fallback for a field whose label is a ReactNode with no string form.
    unitForAria: 'Unit for {{field}}',
    unitAria: 'Unit',
    priorForAria: 'Prior distribution for {{field}}',
    modeToggleAria: 'Sweep mode',
    single: 'Single',
    range: 'Range',
    lowerBound: 'Lower bound',
    upperBound: 'Upper bound',
    prior: 'Prior',
    priorUniform: 'Uniform',
    priorLogUniform: 'Log-uniform',
    sweepCount: '{{count}} values · sweep (one run each)',
    continuous: 'continuous, drawn from study sampling',
  },
  // Result-column labels for the EvalTool + DP tables. Curated wins over
  // generic humanization (`formatResponseKey`) — anything not covered here
  // falls back to English Title Case, so a locale gap never blanks a header.
  // Kept flat per column so the DP table can hydrate one key at a time.
  // Sweep-axis paths shown in preview column headers, override chips,
  // and chart legends. Raw wire paths (`inlet.velocity`, `outlet.pressure`,
  // `rho` / `mu` / `nu`) leak schema jargon; these are the plain names.
  sweepPaths: {
    inletVelocity: 'Inlet velocity',
    outletPressure: 'Outlet pressure',
    // Greek symbols kept alongside so users who know the notation still
    // recognize the field, and the leading noun carries new readers.
    rho: 'Density (ρ)',
    mu: 'Dynamic viscosity (μ)',
    nu: 'Kinematic viscosity (ν)',
    initialTemperature: 'Initial temperature',
    // FEA DOE axes (ENG-2602) — instance-keyed; {{target}} / {{name}} /
    // {{axis}} come from the wire path parser.
    axisFx: 'Fx',
    axisFy: 'Fy',
    axisFz: 'Fz',
    loadVector: '{{target}} · {{axis}}',
    facePressure: '{{target}} · pressure',
    contactFriction: 'Contact {{name}} · friction',
    contactStiffness: 'Contact {{name}} · stiffness',
    contactStickSlope: 'Contact {{name}} · stick slope',
    boltPreload: 'Bolt {{name}} · preload',
    tieTolerance: 'Tie {{name}} · position tolerance',
    thermalValue: '{{target}} · temperature / flux',
    convectionH: '{{target}} · convection h',
    ambientTemperature: '{{target}} · ambient temperature',
    radiationEmissivity: '{{target}} · emissivity',
    gravityMagnitude: 'Gravity · magnitude',
    centrifugalRpm: 'Centrifugal · RPM',
  },
  responseKeys: {
    // Named response keys (CFD objectives).
    nu: 'Kinematic viscosity (ν)',
    rho: 'Density (ρ)',
    outletVelocity: 'Outlet velocity',
    pumpingPower: 'Pumping power',
    thermalResistance: 'Thermal resistance',
    pressureDrop: 'Pressure drop',
    inletFlowRate: 'Inlet flow rate',
    outletFlowRate: 'Outlet flow rate',
    forceX: 'Force X',
    forceY: 'Force Y',
    forceZ: 'Force Z',
    // CHT + solver-run scalars (display-only, not optimizable).
    hottestSolid: 'Hottest solid',
    targetMet: 'Target met',
    targetTemperature: 'Target temperature',
    // "Kinematic" here refers to pressure normalized by density (p/ρ,
    // m²/s²) — distinct from "dynamic pressure" (½ρU², Pa). Keeping the
    // English as-is; JA/zh-TW copy uses the correct kinematic term
    // (運動学的 / 運動學) rather than 動 (which reads as "dynamic").
    pressureDropKinematic: 'Kinematic pressure drop',
    lastTimeStep: 'Last time step',
    timeSteps: 'Time steps',
    // OpenFOAM's ExecutionTime field — the solver-reported wall-clock
    // for a single SIMPLE loop iteration set. "Solver time" reads more
    // scientifically and disambiguates from OS process time.
    executionTime: 'Solver time',
    iterations: 'Iterations',
    converged: 'Converged',
    divergenceDetected: 'Divergence detected',
    // Common humanized leaves surfaced under `regions.<name>.*` and
    // `interface.*` — the segments humanization would otherwise emit
    // literally.
    hasHeatSource: 'Has heat source',
    totalHeatFlux: 'Total heat flux',
    peakHeatFlux: 'Peak heat flux',
    // Path prefixes shown before a leaf in composed labels
    // ("Interface · Total heat flux", "Convergence · Final residuals · U").
    interface: 'Interface',
    volume: 'Volume',
    area: 'Area',
    // Per-patch fluid ↔ solid flux breakdown (BE ENG-1927). Sign
    // convention: positive = net watts crossing from the KEY's FIRST
    // region into the SECOND (`coolant → solid_0 = +713 W` means the
    // coolant is dumping 713 W into solid_0; negative means the other
    // way, which reads as an anomaly on a chip cooler).
    // ENG-1952 per-patch interface breakdown. Wetted area, wall
    // temperature and the wall→bulk ΔT the film coefficient divides by.
    // Per-region material properties. THERMAL conductivity, not electrical,
    // and a declared HEAT load rather than electrical power — the wire names
    // (`conductivity_w_m_k`, `declared_power_w`) say neither, so the label
    // has to.
    thermalConductivity: 'Thermal conductivity',
    specificHeat: 'Specific heat',
    declaredHeatLoad: 'Declared heat load',
    volumeAveragedTemp: 'Volume-averaged temperature',
    initialTemp: 'Initial temperature',
    perPatchArea: 'Per-patch area',
    perPatchWallTemp: 'Per-patch wall temperature',
    perPatchWallBulkDt: 'Per-patch wall→bulk ΔT',
    bulkTemp: 'Bulk temperature',
    totalHeatFluxSolidSide: 'Total heat flux (solid side)',
    interfaceDisagreement: 'Interface disagreement',
    // Energy-balance block: heat in against heat out, and the gap.
    energyBalance: 'Energy balance',
    heatInput: 'Heat input',
    heatRemoved: 'Heat removed',
    // FEA thermal_conduction heat_balance threshold (ENG-2658).
    heatBalanceTolerance: 'Heat-balance tolerance',
    // thermal_transient DP shortlist (ENG-3002 / TH-6).
    peakTemperature: 'Peak temperature',
    peakTime: 'Peak time',
    peakBody: 'Peak body',
    timeReached: 'Time reached',
    nIncrements: 'Increments',
    nFrames: 'Frames',
    bodyTemperatures: 'Body temperatures',
    bodyPeakTemperatures: 'Body peak temperatures',
    surfaceFluxesW: 'Surface fluxes',
    temperatureBounds: 'Temperature bounds',
    energyIn: 'Energy in',
    energyOut: 'Energy out',
    storedEnergyChange: 'Stored energy change',
    energyImbalance: 'Energy imbalance',
    imbalance: 'Imbalance',
    imbalancePct: 'Imbalance',
    verdict: 'Verdict',
    perPatch: 'Per patch',
    solidPerPatch: 'Per patch (solid side)',
    peakFluxPatch: 'Peak flux patch',
    hottestSolidRegion: 'Hottest solid',
    convergence: 'Convergence',
    finalResiduals: 'Final residuals',
    // Region-role prefixes for `regions.<name>.<stat>` composed labels.
    fluid: 'Fluid',
    solid: 'Solid',
    // CHT limiter-clamps sub-tree (`limiter_clamps.velocity_cells`,
    // `.temperature_lower_cells`, ...). Parser writes counts and pct
    // pairs; keep the labels the same on both.
    limiterClamps: 'Limiter clamps',
    velocityCells: 'Velocity cells',
    velocityCellsPct: 'Velocity cells (%)',
    temperatureLowerCells: 'Temperature cells (lower)',
    temperatureUpperCells: 'Temperature cells (upper)',
    temperatureLowerCellsPct: 'Temperature cells (lower, %)',
    temperatureUpperCellsPct: 'Temperature cells (upper, %)',
    temperatureUnlimitedMaxK: 'Temperature unlimited max',
    temperatureUnlimitedMinK: 'Temperature unlimited min',
    // Same base labels the ENG-1917 DP-header registration
    // (`temperature_unlimited_max` etc. in RESPONSE_KEY_LABEL_I18N)
    // reaches after the parser pops the `_k` unit. Duplicated to keep
    // stripped and non-stripped spellings pointing at one string.
    temperatureUnlimitedMax: 'Temperature unlimited max',
    temperatureUnlimitedMin: 'Temperature unlimited min',
    coolantDeltaT: 'Coolant ΔT',
    coolantMassFlow: 'Coolant mass flow',
    coolantMassImbalance: 'Coolant mass imbalance',
    limitTemperatureMax: 'Temperature clamp limit',
    // Runtime-only path segments on `result_summary` — `sim_error.detail.*`
    // carries the classifier's picked failure detail (e.g. mass imbalance
    // on divergence). Kept as plain-language segment labels; the leaf
    // still resolves through its own key.
    simError: 'Sim error',
    detail: 'Detail',
    // Stat suffixes for CHT region min/avg/max temperature + max velocity
    // (`T min`, `T avg`, `T max`, `U max`) and the CFD final-residual
    // template (`Final residual ({{field}})`).
    statMin: 'min',
    statAvg: 'avg',
    statMax: 'max',
    tempStat: 'T {{stat}}',
    velMax: 'U max',
    finalResidual: 'Final residual ({{field}})',
    initialResidual: 'Initial residual ({{field}})',
    // FEA (CalculiX structural static) result-column labels. "von Mises" is a
    // proper name (the stress criterion) — kept verbatim across locales. The
    // unit (MPa / mm) rides in the column header from the DP table's own
    // unit-token parsing, so these are the bare quantity names.
    maxVonMises: 'Max von Mises',
    maxVonMisesNode: 'Max von Mises node',
    maxPrincipal: 'Max principal',
    maxPrincipalNode: 'Max principal node',
    minPrincipal: 'Min principal',
    minPrincipalNode: 'Min principal node',
    maxShear: 'Max shear',
    materialYield: 'Material yield',
    materialUltimate: 'Material ultimate',
    maxDisplacement: 'Max displacement',
    maxDisplacementNode: 'Max displacement node',
    safetyFactorYield: 'Safety factor (yield)',
    safetyFactorUltimate: 'Safety factor (ultimate)',
    // Non-numeric FEA scalars: the pass/fail verdict + the resolved material.
    assessment: 'Assessment',
    materialName: 'Material',
    // external_flow orientation-free wall-load decomposition. Forces are the
    // magnitudes of the pressure (normal) / viscous (tangential) traction;
    // wall pressure is gauge-pressure extrema over the object walls. The unit
    // (N / Pa) rides in the header from responseKeyUnit; ratio is dimensionless.
    forceNormalMag: 'Normal force',
    forceTangentialMag: 'Tangential force',
    normalTangentialRatio: 'Normal / tangential ratio',
    wallPressureMin: 'Wall pressure (min)',
    wallPressureMax: 'Wall pressure (max)',
    // Per-axis wall-traction decomposition — sibling vectors of the
    // `_mag` scalars. Unit (N) rides in from responseKeyUnit.
    forcePressureX: 'Force pressure X',
    forcePressureY: 'Force pressure Y',
    forcePressureZ: 'Force pressure Z',
    forceViscousX: 'Force viscous X',
    forceViscousY: 'Force viscous Y',
    forceViscousZ: 'Force viscous Z',
    // BC integrals + mass balance — DP detail Results.
    inletVelocityAvg: 'Inlet velocity',
    // "Mass imbalance" — |inlet − outlet| / max — dimensionless
    // conservation error, not a rate. "Fraction" would suggest a
    // fraction of something else; the bare quantity name reads cleaner.
    massImbalanceFrac: 'Mass imbalance',
    // Solver-detected non-physical state (negative volume, non-finite
    // residuals, ill-posed BC). Companion to `divergenceDetected`.
    solutionUnphysical: 'Solution unphysical',
    // Force coefficients (external_flow, emitted when force_coeffs is set on
    // sim_params). All dimensionless; unit does not append to the label.
    cd: 'Drag coefficient (C_D)',
    cl: 'Lift coefficient (C_L)',
    cmPitch: 'Pitching moment coefficient (C_M)',
    clCdRatio: 'L/D ratio',
    copFracLref: 'CoP fraction (l_ref)',
    cdBand: 'C_D stability band',
    clBand: 'C_L stability band',
    cdDrift: 'C_D drift',
    clDrift: 'C_L drift',
    // Bulk temperature aggregates over the fluid field (CFD-with-thermal).
    temperatureMin: 'Temperature (min)',
    temperatureAvg: 'Temperature (avg)',
    temperatureMax: 'Temperature (max)',
    // Wall y⁺ aggregates (external_flow parser). Dimensionless.
    yplusMin: 'y⁺ min',
    yplusMean: 'y⁺ mean',
    yplusMax: 'y⁺ max',
    // Mass & inertia — FEA mass properties. Units (kg / kg·m² / m) ride in the
    // column header from responseKeyUnit. Ixx…Iyz are the inertia-tensor
    // components about the assembly centre of mass in global mesh axes; radius
    // of gyration is k = √(I/m) per axis.
    mass: 'Mass',
    inertiaIxx: 'Inertia Ixx',
    inertiaIyy: 'Inertia Iyy',
    inertiaIzz: 'Inertia Izz',
    inertiaIxy: 'Inertia Ixy',
    inertiaIxz: 'Inertia Ixz',
    inertiaIyz: 'Inertia Iyz',
    radiusOfGyrationX: 'Radius of gyration X',
    radiusOfGyrationY: 'Radius of gyration Y',
    radiusOfGyrationZ: 'Radius of gyration Z',
    // y⁺ area-fraction bands — how much of the wetted wall sits in each
    // log-law-relevant range. Companion to yplusMin/Mean/Max.
    yplusFracBelow5: 'y⁺ area fraction (< 5)',
    yplusFrac5To30: 'y⁺ area fraction (5–30)',
    yplusFrac30To100: 'y⁺ area fraction (30–100)',
    yplusFracAbove100: 'y⁺ area fraction (> 100)',
    // Solver-run diagnostics surfaced in the DP-table column picker.
    // Hidden by default via isDiagnosticResultKey but the picker labels
    // them; register so the checkbox reads localized.
    cumulativeContinuity: 'Cumulative continuity',
    solverFailed: 'Solver failed',
    velocityRunaway: 'Velocity runaway',
    singularMatrixDetected: 'Singular pressure matrix',
    hintBcIc: 'Hint: check BCs / ICs',
    hintMeshQuality: 'Hint: check mesh quality',
    hintTurbulence: 'Hint: check turbulence',
    exitCode: 'Exit code',
    maxPLinearIters: 'Max pressure solver iterations',
    divergenceOnsetStep: 'Divergence onset (step)',
    firstDivergedField: 'First diverged field',
    solverMpiRanks: 'Solver MPI ranks',
    totalPressureDropPa: 'Total pressure drop',
    totalPressureDropKinematic: 'Total pressure drop (kinematic)',
    zeta0: 'ζ₀ (total-pressure loss coeff.)',
    zetaP: 'ζₚ (static-pressure loss coeff.)',
    velocity: 'Velocity',
    pressure: 'Pressure',
    density: 'Density',
    temperature: 'Temperature',
    angleOfAttackDeg: 'Angle of attack α (deg)',
    sideslipDeg: 'Sideslip β (deg)',
    inlet: 'Inlet',
    outlet: 'Outlet',
  },
  // Plain-language names for the OpenFOAM field symbols (ENG-1922).
  // Rendered as "name (symbol)" by `fieldDisplayName` wherever a field is
  // picked or legended, so a reader chooses "Velocity (U)" rather than a
  // bare "U". Only this half is translated: the symbol is generated from
  // `FIELD_SYMBOLS` and stays verbatim in every locale, because it is
  // equation notation rather than prose. (The tooltip glossary below is
  // the opposite case — prose, and translated.)
  // OpenFOAM field-name glossary — hover tooltips on residual / result
  // columns whose header is a bare CFD symbol (U, p, k, ω, ε, νₜ, αₜ,
  // p_rgh, h). Same content as the `SOLVER_FIELD_TOOLTIPS` map in
  // `response-keys.ts` — the English map stays as the fallback / source
  // of truth; the i18n keys below drive `fieldTooltip(key, t)` so a
  // JA/zh-TW user hovering these columns gets a localized explanation
  // rather than the raw English reference text.
  fieldNames: {
    displacement: 'Displacement magnitude',
    displacement_x: 'Displacement x',
    displacement_y: 'Displacement y',
    displacement_z: 'Displacement z',
    von_mises: 'von Mises stress',
    max_principal: 'Max principal stress',
    min_principal: 'Min principal stress',
    max_shear: 'Max shear stress',
    temperature: 'Temperature',
    U: 'Velocity',
    Ux: 'Velocity x',
    Uy: 'Velocity y',
    Uz: 'Velocity z',
    p: 'Pressure',
    p_rgh: 'Modified pressure',
    T: 'Temperature',
    h: 'Enthalpy',
    k: 'Turbulent kinetic energy',
    epsilon: 'Dissipation rate',
    omega: 'Specific dissipation rate',
    nut: 'Turbulent viscosity',
    nuTilda: 'Modified turbulent viscosity',
    alphat: 'Turbulent thermal diffusivity',
    nut_over_nu: 'Viscosity ratio',
    rho: 'Density',
    phi: 'Flux',
    wallShearStress: 'Wall shear stress',
    vorticity: 'Vorticity',
  },
  fieldTooltips: {
    von_mises:
      'Equivalent tensile stress — the scalar a ductile yield check compares against. Sign-blind by construction: a part in pure compression reports the same number as one in tension at the same magnitude, and a pure-shear state reads √3 too high. Read the principals beside it when the direction of the load matters.',
    max_principal:
      'Greatest principal stress σ1. Signed, so positive is tension — this is what says whether a peak is opening a crack or closing one, and it is the measure a brittle material is graded against.',
    min_principal:
      'Least principal stress σ3. Signed; the most negative point is the most compressive in the part. Read it beside σ1 to tell a bearing contact from a tensile hotspot.',
    max_shear:
      "Peak shear on any plane, Tresca's (σ1 − σ3) / 2. The governing quantity for a bolted or bonded joint that slips rather than stretches, and the basis of the Tresca criterion where a spec calls for it instead of von Mises.",
    // Mass & inertia (FEA). The frame is repeated on every tensor
    // component deliberately — the backend does not emit the centre of
    // mass, so this is the only place it is stated.
    mass: 'Total mass — the sum over every solid body of its CAD volume times the density of the material assigned to it. Extracted fluid cavities are excluded.',
    inertiaIxx:
      'Moment of inertia about the x-axis, about the assembly centre of mass, in global mesh axes.',
    inertiaIyy:
      'Moment of inertia about the y-axis, about the assembly centre of mass, in global mesh axes.',
    inertiaIzz:
      'Moment of inertia about the z-axis, about the assembly centre of mass, in global mesh axes.',
    inertiaIxy:
      'Product of inertia xy, about the assembly centre of mass, in global mesh axes. Non-zero means the global axes are not principal axes.',
    inertiaIxz:
      'Product of inertia xz, about the assembly centre of mass, in global mesh axes.',
    inertiaIyz:
      'Product of inertia yz, about the assembly centre of mass, in global mesh axes.',
    radiusOfGyrationX:
      'Radius of gyration about x — √(Ixx/m), about the global axis rather than a principal axis.',
    radiusOfGyrationY:
      'Radius of gyration about y — √(Iyy/m), about the global axis rather than a principal axis.',
    radiusOfGyrationZ:
      'Radius of gyration about z — √(Izz/m), about the global axis rather than a principal axis.',
    p_rgh:
      'Modified pressure p − ρgh (hydrostatic head removed) — what the CHT solver solves. Kinematic (p/ρ, m²/s²); switch units to read absolute Pa/psi via ρ. Residual tracks continuity / mass-conservation convergence.',
    p: 'Static pressure — incompressible OpenFOAM solves it kinematic (p/ρ, m²/s²); switch units to read absolute Pa/psi via ρ. Residual tracks continuity / mass-conservation convergence.',
    U: 'Velocity vector — momentum equation residual (3-component magnitude).',
    Ux: 'Velocity x-component — momentum equation residual.',
    Uy: 'Velocity y-component — momentum equation residual.',
    Uz: 'Velocity z-component — momentum equation residual.',
    h: 'Enthalpy — energy equation residual (CHT solid + fluid temperature coupling).',
    k: 'Turbulence kinetic energy — k-ε / k-ω model residual.',
    omega:
      'Specific dissipation rate ω — k-ω / SST model residual (eddy-viscosity scale).',
    epsilon: 'Turbulence dissipation rate ε — k-ε model residual.',
    nut: 'Turbulent eddy viscosity νₜ — derived field; residual indicates closure stability.',
    alphat:
      'Turbulent thermal diffusivity αₜ — derived field coupling turbulence to the energy equation.',
    temperature:
      'Steady-state temperature field in kelvin — CalculiX conduction (NDTEMP) or conjugate heat transfer.',
    pressure_drop_pa:
      'Static pressure drop across the domain (inlet − outlet), in absolute Pa.',
    pumping_power_w:
      'Ideal hydraulic power to push the coolant through the domain: pressure drop × volumetric flow rate (Δp × ṁ/ρ). This excludes pump efficiency and motor losses.',
    pressure_drop_kinematic:
      'The same pressure drop expressed kinematically (Δp/ρ, m²/s²) — the absolute Pa value divided by fluid density ρ.',
    tempStat:
      'Steady-state temperature extremum over this region — the min / avg / max of the converged temperature field.',
    velMax: 'Peak velocity magnitude in this region at convergence.',
    force_normal_mag:
      'Magnitude of the total pressure (normal) force on the object walls. Orientation-free — reads the same regardless of the flow axis, so it works as a direct wall-load reading without picking a Cx/Cy/Cz component.',
    force_tangential_mag:
      'Magnitude of the total viscous (tangential) force on the object walls — the skin-friction contribution. Orientation-free.',
    normal_tangential_ratio:
      'Normal (pressure) ÷ tangential (viscous) wall force. High = pressure-dominated loading; low = friction-dominated. Dimensionless; null when tangential force is zero.',
    wall_pressure_min_pa:
      'Minimum gauge pressure over the object wall faces, in absolute Pa. Useful as a wall-load input for structural sizing.',
    wall_pressure_max_pa:
      'Maximum gauge pressure over the object wall faces, in absolute Pa. Useful as the peak wall-load input for structural sizing.',
    force_pressure_x:
      'X component of the pressure (normal) force on the object walls. Sum of p·n over all wall faces.',
    force_pressure_y:
      'Y component of the pressure (normal) force on the object walls.',
    force_pressure_z:
      'Z component of the pressure (normal) force on the object walls.',
    force_viscous_x:
      'X component of the viscous (tangential) force on the object walls — skin friction contribution.',
    force_viscous_y:
      'Y component of the viscous (tangential) force on the object walls.',
    force_viscous_z:
      'Z component of the viscous (tangential) force on the object walls.',
    inlet_flow_rate_m3s:
      'Volumetric flow rate through the inlet face(s) at convergence. Sign: positive into the domain. Should match the outlet within Mass Imbalance Frac.',
    outlet_flow_rate_m3s:
      'Volumetric flow rate through the outlet face(s) at convergence. Sign: positive out of the domain.',
    outlet_velocity_avg_ms:
      'Area-weighted average velocity magnitude on the outlet face(s) at convergence.',
    inlet_velocity_avg_ms:
      'Area-weighted average velocity magnitude on the inlet face(s) at convergence — should match the study inlet velocity BC.',
    mass_imbalance_frac:
      '|inlet − outlet| / max(|inlet|, |outlet|) — fractional mass conservation error at convergence. Under 1e-4 is typical; over 1e-2 signals continuity is not fully closed.',
    time_step_count:
      'Total number of solver iterations (or time steps for transient) the run executed before hitting either the residual target or the max iteration cap.',
    last_time_step:
      'Value of the final iteration counter written by the solver. Matches the total step count on a normal run; may differ if the solver was interrupted.',
    execution_time_s:
      'Wall-clock time the solver spent iterating, in seconds. Excludes case setup, mesh conversion, and result extraction.',
    solution_unphysical:
      'True when the solver detected non-physical values in the converged field — negative volume, non-finite residuals, or ill-posed BCs.',
    divergence_detected:
      'True when residuals grew across iterations rather than shrinking — the solver did not converge and results should not be trusted.',
    cd: 'Drag coefficient (C_D) — pressure + viscous force along the flow direction, normalized by ½·ρ·U²·A_ref. Dimensionless.',
    cl: 'Lift coefficient (C_L) — pressure + viscous force perpendicular to flow, normalized by ½·ρ·U²·A_ref. Dimensionless.',
    cm_pitch:
      'Pitching moment coefficient (C_M) — moment about the configured pitch axis (perpendicular to the flow axis), normalized by ½·ρ·U²·A_ref·l_ref. Sign follows the right-hand rule: positive C_M = counter-clockwise rotation about the +pitch-axis direction; negative = clockwise. Whether that reads as nose-up or nose-down depends on which XYZ axis was picked as the pitch axis and how the body is oriented in the domain. Dimensionless.',
    cl_cd_ratio:
      'Lift-to-drag ratio — C_L / C_D. Aerodynamic efficiency proxy. Dimensionless.',
    cop_frac_lref:
      'Center of pressure (CoP) — where along the reference-chord axis the pressure distribution acts as a single point force. Reported as a fraction of the reference chord (l_ref) from the moment-reference point, measured along the pitch axis (perpendicular to the flow and lift axes). 0 = at the moment reference, 0.25 = quarter-chord (the classical aerodynamic centre for thin airfoils), 1.0 = one chord downstream. Dimensionless. Null when the aerodynamic force or moment is zero.',
    cd_band:
      'Half-width of the C_D envelope over the last iteration window — how much drag was still oscillating at the end of the run. Near zero = value has settled; large = increase iterations before trusting C_D.',
    cl_band:
      'Half-width of the C_L envelope over the last iteration window — how much lift was still oscillating at the end of the run. Near zero = value has settled; large = increase iterations before trusting C_L.',
    temperature_min:
      'Minimum steady-state fluid temperature over the domain, in K.',
    temperature_avg:
      'Volume-weighted average fluid temperature at convergence, in K.',
    temperature_max:
      'Maximum steady-state fluid temperature over the domain, in K.',
    yplus_min:
      'Minimum wall y⁺ over the object wall faces at convergence. Under 5 = viscous sublayer resolved (needs low-Re model); under 30 with wall functions = under-resolved.',
    yplus_mean:
      'Area-weighted mean wall y⁺ over the object wall faces at convergence. Best single-number sanity check for near-wall mesh sizing.',
    yplus_max:
      'Maximum wall y⁺ over the object wall faces at convergence. Above 300 with wall functions = log-law model is out of its valid range; shear + heat transfer will be off.',
    yplus_frac_below_5:
      'Fraction of wetted-wall area where y⁺ < 5 at convergence — the viscous-sublayer band. High values on a wall-function run mean the near-wall mesh is over-resolved for the model; a low-Re / wall-resolving model wants this to dominate.',
    yplus_frac_5_to_30:
      'Fraction of wetted-wall area where 5 ≤ y⁺ < 30 at convergence — the buffer layer, valid for NEITHER wall functions nor low-Re resolved BLs. Any run with meaningful area here has shear + heat transfer computed inside a modelling gap; refine or coarsen the near-wall mesh to move it out.',
    yplus_frac_30_to_100:
      'Fraction of wetted-wall area where 30 ≤ y⁺ ≤ 100 at convergence — the sweet spot for standard log-law wall functions. Higher = healthier near-wall mesh sizing for a wall-function run.',
    yplus_frac_above_100:
      'Fraction of wetted-wall area where y⁺ > 100 at convergence — above the log-law valid range. Meaningful area here on a wall-function run means shear + heat transfer are being evaluated outside the model, and downstream drag / lift / heat flux are unreliable.',
    solver_failed:
      'True when the solver exited non-zero or was killed. The failure banner says the same thing in words; this is the machine-readable flag the retry logic branches on.',
    velocity_runaway:
      'True when the velocity magnitude grew without bound before the run stopped. Distinguishes a genuinely diverging momentum solve from one that merely ran out of iterations.',
    singular_matrix_detected:
      'True when OpenFOAM reported a singular matrix. On a closed domain this almost always means no pressure reference was pinned, so the pressure field has no absolute level to solve for.',
    hint_bc_ic:
      'Set when the failure pattern points at the boundary or initial conditions — a singular pressure matrix, a continuity abort, or a run that died in under ten iterations.',
    hint_mesh_quality:
      'Set when the pressure solver needed 50 or more iterations in any single step — one bad step is enough to raise it. Points at ill-conditioned cells: high non-orthogonality or extreme aspect ratio.',
    hint_turbulence:
      'Set when a turbulence field (k, ω or ε) went NaN before pressure or velocity did, pointing at the turbulence model rather than the momentum solve. Never set on a laminar run, which solves none of those fields.',
    exit_code:
      'Exit status reported by the solver process. Absent when the container was killed rather than exiting on its own — a timeout or an out-of-memory kill leaves none.',
    max_p_linear_iters:
      'The highest iteration count the pressure linear solver needed in any single step. Consistently high values are what the mesh-quality hint keys on.',
    divergence_onset_step:
      'The step at which a residual first started growing rather than falling. Null when no divergence was detected.',
    first_diverged_field:
      'The field whose residual grew first. The one that went first is usually the one to fix — a turbulence field points somewhere different from pressure.',
    cumulative_continuity:
      'Running mass-conservation error across iterations. Should decay together with the pressure residual; a plateau or growth signals continuity is not fully closed (BC mismatch, mesh non-orthogonality, or an under-relaxed pressure solve).',
    solver_mpi_ranks:
      'Number of MPI worker ranks the solver decomposed the domain onto. Parallelism topology only — does not affect physics; useful for debugging a failed run where partitioning may have been the culprit.',
    sim_error_yplus_min:
      'The lowest wall y+ on the flagged run, shown for context — it is one face, often a corner or a stagnation point, and does not decide the flag on its own. The low side of the y+ check judges wall-area fractions and the turbulence model: it flags more than 10% of the wall in the y+ 5–30 buffer band on any model, and more than 10% below y+ 5 only on k-ε with log-law wall functions. k-ω SST meshed to y+ ≈ 1 is a resolved wall, not a fault. Runs from before this rule were flagged on this single value alone.',
    // ---- CHT (conjugate heat transfer) result rows ----
    chip_t_max_k:
      'Hottest solid-region temperature across every region flagged as a heat source. Drives the target-met pass/fail badge against the target temperature.',
    target_temperature_k:
      'User-set temperature limit the run is checked against. When null, the target-met verdict is not shown (a hardcoded 70 °C limit would falsely pass every non-electronics case).',
    target_met:
      'Whether chip_t_max_k stayed below target_temperature_k. Null when no target was set.',
    coolant_mass_flow_kg_s:
      'Coolant mass flow through the fluid region at convergence. Emitted on newer solver runs; older runs render this blank.',
    coolant_delta_t_k:
      'Coolant enthalpy rise across the fluid region — outlet temperature minus inlet. Combined with ṁ · c_p, gives the electrical power the coolant actually removed.',
    coolant_mass_imbalance_kg_s:
      'Solver-reported mass imbalance between coolant inlet and outlet. Should be ~0 on a converged run; a non-zero value means the mass balance did not close to rounding.',
    total_heat_flux_w:
      'Total watts crossing EVERY fluid ↔ solid interface in this run, summed. Each patch is one coolant ↔ solid pair (coolant ↔ solid_0, coolant ↔ solid_1, …); the number here is the sum across all of them. As stored, positive means heat INTO the solids, so a run whose solids heat the coolant (the normal cooling direction) reads NEGATIVE; the heat the coolant gains is this number with its sign flipped. Per-patch splits appear on the region-graph schematic when the run records them; compare the total against the declared electrical input on the consistency check panel.',
    peak_heat_flux_w_m2:
      'Largest local heat flux anywhere on the fluid ↔ solid interface. High values mark hot spots that may need mesh refinement. The patch name of the peak is on the region-graph schematic when the run records it.',
    interfacePerPatch:
      'Watts crossing one interface patch, as stored: positive = heat flowing INTO the solid side of the patch (for a solid ↔ solid patch, into the region named first). A solid giving heat to its coolant reads NEGATIVE, which is the normal cooling direction; a positive value on a coolant patch means the coolant is heating that solid.',
    t_min_k: 'Minimum temperature in this region at convergence.',
    t_avg_k: 'Volume-averaged temperature in this region at convergence.',
    t_max_k: 'Maximum temperature in this region at convergence.',
    u_max_m_s: 'Peak velocity magnitude in this region at convergence.',
    iterations_run:
      'How many SIMPLE iterations the solver executed. Reaching the iteration budget without converging means the run should be treated as a snapshot, not a steady solution.',
    converged:
      'Whether the residuals dropped below their configured tolerance before the iteration budget ran out.',
    velocity_cells:
      'Count of cells the velocity limiter clamped at the final iteration. Non-zero means the solver was actively holding U down to keep the run alive — treat downstream quantities as indicative until the limited region is meshed or driven better.',
    velocity_cells_pct:
      'Percent of the fluid mesh clamped by the velocity limiter at the final iteration. > 0.1 % means the run converged on a field the solver had to constrain.',
    temperature_lower_cells:
      'Count of cells the temperature lower-limiter clamped. Non-zero means T fell below the safe floor somewhere in the domain and had to be held up.',
    temperature_upper_cells:
      'Count of cells the temperature upper-limiter clamped. Non-zero means T ran above the safe ceiling somewhere in the domain and had to be held down.',
    temperature_lower_cells_pct:
      'Percent of cells the temperature lower-limiter clamped.',
    temperature_upper_cells_pct:
      'Percent of cells the temperature upper-limiter clamped.',
    temperature_unlimited_max_k:
      'Highest temperature the solver saw before any clamp fired. A large gap between this and the clamp limit means the solver had to reach further to hold the field down.',
    temperature_unlimited_min_k:
      'Lowest temperature the solver saw before any clamp fired.',
    limit_temperature_max:
      'Ceiling the temperature limiter was configured with. Cells above this were reset to it during each iteration.',
    n_increments:
      'How many physical-time increments CalculiX took to reach the cycle period. Each increment is one Δt advance inside the heat-transfer step.',
    n_frames:
      'How many temperature frames were written for the results timeline. Set by the output interval — roughly cycle period ÷ interval + 1 when the interval divides the period evenly.',
    time_reached:
      'Physical time the solver actually reached. Matches the cycle period on a completed run; shorter when the solve stopped early.',
    time_reached_s:
      'Physical time the solver actually reached. Matches the cycle period on a completed run; shorter when the solve stopped early.',
    surface_fluxes_w:
      'Face heat rate on the solid (W), as stored on the run: negative heats the solid; positive cools it. The heat-balance card flips the sign so red chips read as heating.',
    peak_temperature_K:
      'Hottest temperature anywhere in the solid over the whole period — not just the final frame. Peak time and peak body name the when and where.',
    energy_in_j:
      'Heat energy that entered the solid over the period (∫Q_in·dt), integrated from the per-increment boundary face rates.',
    energy_out_j:
      'Heat energy that left the solid over the period (∫Q_out·dt), integrated from the per-increment boundary face rates.',
    stored_energy_change_j:
      'Change in thermal energy stored in the solid over the period (ρ·c_p·∫V ΔT). Closes the energy books with ∫Q_in and ∫Q_out — not the same as the final-step face-integral mesh check.',
    energy_imbalance_j:
      'Energy books residual: ∫Q_in − ∫Q_out − ΔE_stored. A large value means the period ledger did not close (mesh resolution on recovered-gradient faces can widen it).',
  },
  cht: {
    // ENG-1912 — declared geometry the Hand-Calc anchors panel and the
    // 6-axis radar read. Never handed to the case writer.
    // Shown under a geometry hint typed as zero or negative. `SimGeometry`
    // declares both fields `gt=0`, so the value would ride to the wire and
    // come back a 422 with nothing pointing at the field that caused it.
    mustBePositive: 'Must be greater than zero.',
    handCalcGeometry: 'Hand-Calc Geometry',
    // Symbol + unit are composed in JSX with `normal-case` (RowLabel would
    // otherwise turn D_h (m) into D_H (M)).
    hydraulicDiameter: 'Hydraulic Diameter',
    hydraulicDiameterInfo:
      'Hydraulic diameter of the coolant passage, 4·A/P for the wetted cross-section. Seeds the D_h row on the report’s Hand-Calc panel and closes the Nusselt and j-factor radar axes. Not a solver input — leaving it blank changes nothing about the run.',
    characteristicLength: 'Characteristic Length',
    characteristicLengthInfo:
      'Length heat conducts through in the solid, usually a wall or base thickness. Seeds the L row on the report’s Hand-Calc panel and closes the Biot radar axis. Not a solver input.',
    fluid: 'Fluid',
    // CHT fluid picker — thermal-aware library. Distinct from the CFD fluid
    // presets (`simopsSolver.fluidProps.presets.*`): CHT ships water,
    // glycol-water coolants, and dry air (ENG-3070), all temperature-anchored.
    // Same "translated · English canonical" pattern the FEA + CFD pickers
    // established: `fluidMaterialsFull` expands the industry EGW / PG
    // abbreviations for users who wouldn't recognize the acronym. Air has
    // no expansion — the short label already says what it is (same as water).
    fluidMaterials: {
      water_20c: 'Water (20 °C)',
      egw_50_50_25c: 'EGW 50/50 (25 °C)',
      pg_25_25c: 'PG 25 (25 °C)',
      air_20c: 'Air (20 °C)',
    },
    fluidMaterialsFull: {
      egw_50_50_25c: 'Ethylene glycol–water 50/50 mix',
      pg_25_25c: 'Propylene glycol–water 25% mix',
    },
    syncAria: 'Sync fluid properties to inlet temperature',
    syncUnsupported:
      'Temperature-interpolated properties coming soon for {{material}}. Only water has a temperature table today.',
    syncNeedsInletTemp:
      'Set an inlet temperature first — sync uses it to look up water properties at the operating point.',
    syncCompute:
      'Compute density, viscosity, Cp, and k for water at the current inlet temperature ({{kelvin}} K / {{celsius}} °C) and write them as overrides. Valid 0–100 °C; clamped outside that range.',
    density: 'Density',
    densityInfo:
      "Mass per unit volume. Override the preset's default by typing a value.",
    kinematicViscosity: 'Kinematic Viscosity',
    kinematicViscosityInfo:
      'Kinematic viscosity (ν = μ/ρ). Accepts scientific notation like 1.5e-5.',
    specificHeat: 'Specific Heat',
    specificHeatInfo:
      'Specific heat at constant pressure. Energy required to raise 1 kg of fluid by 1 K.',
    thermalConductivity: 'Thermal Conductivity',
    thermalConductivityInfo:
      'How well the fluid carries heat. The Prandtl number below is worked out from this and the three fields above.',
    prandtlTabulated:
      'not used — transport is tabulated, so the case file carries k directly and has no Pr entry.',
    prandtlDerived: 'from ρ, ν, Cp and k',
    prandtlPinned:
      'pinned on this case; k above is derived from it, so editing ρ, ν or Cp moves k. Type a conductivity to take over.',
    inlet: 'Inlet',
    velocity: 'Velocity',
    velocityInfo: 'Inlet velocity magnitude along the flow axis.',
    inletTemperature: 'Inlet Temperature',
    inletTemperatureInfo: 'Temperature of the fluid entering the domain.',
    solidRegion: 'Solid region',
    material: 'Material',
    materialInfo:
      'Pick the solid material — density, conductivity, and specific heat below come from the material library.',
    selectMaterial: 'Select material…',
    heatSource: 'Heat Source',
    heatSourceInfo:
      'How this body dissipates heat. Total power is what a datasheet gives you; power density is watts per cubic metre. "Conduction only" states that the body generates nothing, which is different from leaving it unspecified.',
    heatSourceKind: {
      undeclared: 'Not specified',
      none: 'Conduction only (no heat generated)',
      totalPower: 'Total power (W)',
      powerDensity: 'Power density (W/m³)',
    },
    totalPower: 'Total Power',
    totalPowerNote:
      'Divided by this region’s meshed volume at solve time, so it cannot drift from the mesh.',
    volumetricHeatSource: 'Volumetric Heat Source',
    volumetricHeatSourceNote: 'Uniform power density across the whole region.',
    egValue: 'e.g. {{value}}',
    solidRegions: 'Solid Regions',
    noSolidRegionsTitle: 'No solid regions detected',
    noSolidRegionsBodyPre:
      'CHT requires at least one solid region. Classify your CAD bodies in the Mesh step — body roles flow into',
    noSolidRegionsBodyPost: 'and seed the regions list here.',
    solidFaceBcs: 'Solid Face BCs',
    loadingMeshPatches: 'Loading mesh patches…',
    noSolidFacesTitle: 'No solid-side faces tagged yet',
    noSolidFacesBody:
      "Tag solid surfaces in the Mesh step (Boundary Assignment) to assign per-face thermal BCs here. Fluid inlet/outlet faces don't appear in this list — they're controlled above. Fluid↔solid interface patches are wired automatically by the solver.",
    facesCount: '{{count}} faces',
    faceBcKindInfo:
      'Adiabatic = perfectly insulated face (no heat flux). Fixed Temperature = surface held at a target T. Convection = heat exchange with ambient via a convection coefficient h.',
    bcKind: {
      adiabatic: 'Adiabatic',
      fixedTemperature: 'Fixed Temperature',
      convection: 'Convection',
    },
    temperature: 'Temperature',
    temperatureInfo: 'Surface temperature held fixed at this value.',
    convectionCoefficient: 'Convection Coefficient',
    convectionCoefficientInfo:
      'Heat-transfer coefficient h. Natural air ~5, forced air ~50, water ~1000+, boiling ~10k+.',
    ambientTemperature: 'Ambient Temperature',
    ambientTemperatureInfo: 'Free-stream temperature far from the surface.',
    adiabaticNote: 'No heat flux on this face — perfectly insulated.',
    flowType: 'Flow Type',
    flowTypeInfo:
      'Turbulence model. k-ω SST resolves the boundary layer (needs y+ ≲ 5 mesh); k-ε / k-ω use wall functions (y+ 30–300); laminar for low-Re flows (Re ≲ 2000).',
    flowTypeLaminar: 'Laminar',
    flowTypeKEpsilon: 'k-ε (Standard)',
    flowTypeKOmega: 'k-ω',
    flowTypeKOmegaSst: 'k-ω SST',
    turbulence: 'Turbulence',
    intensity: 'Intensity',
    intensityTooltip:
      'Ratio of velocity fluctuation to mean flow (u′/U). Sets initial turbulent kinetic energy k. Typical: 0.01–0.05 external, 0.05–0.10 internal.',
    lengthScale: 'Length Scale (m)',
    lengthScaleTooltip:
      "Characteristic eddy size used to set initial ω and ε. Rule of thumb: ~7–10% of the geometry's characteristic length (chord, diameter, span). Defaults to 0.1 m.",
    convergenceCriteria: 'Convergence Criteria',
    maxIterations: 'Max Iterations',
    maxIterationsInfo:
      'Maximum solver iterations. Leave empty to use the default for your turbulence model (laminar: 1500, RAS: 2000).',
    residualFluid: 'Residual {{key}}',
    residualFluidInfo:
      'Convergence target on {{key}}. Lower = stricter; typical 1e-4 to 1e-6.',
    residualSolid: 'Residual {{key}} (solid)',
    residualSolidInfo:
      'Convergence target on solid-region enthalpy — the field the solid energy equation actually solves. Conduction tends to converge tighter than fluid fields.',
    solverSettings: 'Solver Settings',
    moreOverridesTitle: 'Schemes, Wall Functions, Output',
    writeInterval: 'Write Interval',
    writeIntervalInfo:
      'Iteration interval between solution-field snapshots. Smaller = more frames, more disk usage.',
    relaxU: 'Relax U',
    relaxUInfo:
      'Under-relaxation factor on velocity. Lower = more stable, slower convergence.',
    relaxP: 'Relax P',
    relaxPInfo:
      'Under-relaxation factor on pressure. Leave empty to use the default for your turbulence model (laminar: 0.3, RAS: 0.2).',
    relaxH: 'Fluid energy relaxation',
    limitVelocityFactor: 'Velocity Limit Factor',
    limitVelocityFactorInfo:
      'Velocity cap as a multiple of the inlet speed (limitVelocity fvOption). A local spike in one bad cell should not destroy a solve whose bulk field is fine. 0 disables the limiter.',
    limitTemperatureMin: 'Temperature Limit (min)',
    limitTemperatureMinInfo:
      'Lower bound on fluid temperature (limitTemperature fvOption). Stored in kelvin; pick °C / °F in the unit menu. Leave empty to derive a band around the inlet temperature — empty does not turn the limiter off. Set both min and max together so the band cannot invert against the derived other side.',
    limitTemperatureMax: 'Temperature Limit (max)',
    limitTemperatureMaxInfo:
      'Upper bound on fluid temperature (limitTemperature fvOption). Stored in kelvin; pick °C / °F in the unit menu. Leave empty to derive a band around the inlet temperature — empty does not turn the limiter off. Set both min and max together so the band cannot invert against the derived other side.',
    nonOrthCorrectorsFluid: 'Non-Orth Correctors (fluid)',
    nonOrthCorrectorsFluidInfo:
      'Extra pressure-correction passes per iteration on the fluid region, compensating for skewed cells. Separate from the solid figure because the fluid side carries the boundary layer. Default: 3.',
    nonOrthCorrectorsSolid: 'Non-Orth Correctors (solid)',
    nonOrthCorrectorsSolidInfo:
      'Extra non-orthogonal correction passes per iteration on each solid region. On a tetrahedral mesh the conduction term itself needs them: without, thin solid layers conduct too well. Default: 2.',
    laplacianLimiterSolid: 'Laplacian Limiter (solid)',
    laplacianLimiterSolidInfo:
      'How much of the non-orthogonal correction the solid conduction term keeps. 1 is the full correction; the 0.33 the fluid uses made thin tet layers conduct about 14% too well. Default: 1.',
    relaxHInfo:
      'Controls fluid energy updates. Default: 0.5. Also used for solids when their field is empty.',
    advanced: 'Advanced',
    relaxHSolid: 'Solid energy relaxation',
    relaxHSolidInfo:
      'Controls energy updates in all solids. Leave empty to use the fluid value. Lower values make smaller updates.',
    validation: {
      inletVelocityRequired: 'Fluid inlet velocity is required.',
      inletTemperatureRequired: 'Fluid inlet temperature is required.',
      openingNeedsInlet:
        'Pick which port coolant enters through. The part has more than two ports, so the mesher numbered them instead of guessing a direction — with none marked as an inlet there is nothing driving the flow.',
      openingNeedsOutlet:
        'Pick which port coolant leaves through. With no outlet the coolant has nowhere to go, and the run cannot be set up.',
      openingsUnassigned_one:
        'Port {{patches}} has no direction set, so it will be sealed shut. Set it if coolant should flow through it.',
      openingsUnassigned_other:
        'Ports {{patches}} have no direction set, so they will be sealed shut. Set them if coolant should flow through them.',
      inletTemperatureImplausible:
        'Inlet temperature {{kelvin}} K is implausible — values are absolute Kelvin (room temperature is 293.15 K). Did you mean to enter °C and forget to convert?',
      noFluidRegion: 'CHT requires exactly one fluid region — none configured.',
      tooManyFluidRegions:
        'CHT allows only one fluid region; {{count}} configured.',
      noSolidRegion:
        'CHT requires at least one solid region — none configured.',
      unknownFluidMaterial:
        "Unknown fluid material '{{material}}' in region '{{region}}'.",
      fluidPropsStale:
        'Fluid properties are anchored at 20 °C, but inlet temperature is {{celsius}} °C — click the sync button (↻) to recompute density, ν, Cp, and k at the operating point.',
      inletTempOutsideWaterRange:
        'Inlet temperature {{celsius}} °C is outside the validated water-properties range (0–100 °C); values are clamped at the endpoint.',
      unknownSolidMaterial:
        "Unknown solid material '{{material}}' in region '{{region}}'.",
      noSpecificHeat:
        "Solid region '{{region}}': material '{{material}}' has no specific heat.",
      heatSourceNonPositive:
        "Solid region '{{region}}': heat source must be greater than 0 {{unit}} (got {{value}}). Choose “Conduction only” if this body generates no heat.",
      heatSourceOutOfRange:
        "Solid region '{{region}}': heat source {{value}} {{unit}} is outside the typical engineering range ({{lo}}–{{hi}} {{unit}}) — verify the units.",
      regionKeyMismatch:
        "Region key '{{key}}' doesn't match any mesh body label — the solver won't find constant/{{key}}/polyMesh and the fluid↔solid coupling will be empty. Rename the region to match the mesh.",
      noHeatSource:
        'No heat source defined — simulation will be isothermal. Set a heat source on a solid region, or a Fixed Temperature / Surface Heat Flux / Convection BC on a solid face.',
      staleFaceBc:
        "Boundary condition references face '{{faceId}}' which isn't in the selected mesh — it would silently become adiabatic at solve. Remove the stale BC or pick a mesh that has this patch.",
    },
  },
} as const;
